import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { ApiContractError, ApiError } from '@/api/client'
import { PostgrestError } from '@/api/postgrest/client'
import { FunnelAreaNav } from '@/features/funnels/FunnelAreaNav'
import {
  sumApprovedFunnelRows,
  type ApprovedFunnelRow,
} from '@/features/funnels/approvedFunnelRows'
import {
  useApprovedFunnelSummaries,
  useApprovedFunnelTopics,
} from '@/features/funnels/useApprovedFunnels'
import { formatNumber } from '@/lib/format'

const PAGE_SIZE = 20

function positiveInteger(value: string | null, fallback: number): number {
  if (value === null) return fallback
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

interface FunnelCardProps {
  readonly funnel: ApprovedFunnelRow
}

function FunnelCard({ funnel }: FunnelCardProps): ReactNode {
  const [expanded, setExpanded] = useState(false)
  const {
    data: topics,
    isLoading,
    error,
  } = useApprovedFunnelTopics(funnel.producttypeKey, expanded)

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded((current) => !current)
        }}
        className="w-full p-5 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 dark:hover:bg-slate-700/40 sm:p-6"
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span aria-hidden className="text-lg text-sky-700 dark:text-sky-300">
                {expanded ? '▾' : '▸'}
              </span>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {funnel.producttypeName}
              </h2>
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100">
                Freigegeben
              </span>
            </div>
            <p className="mt-2 max-w-xl pl-6 text-xs text-slate-500 dark:text-slate-400">
              {funnel.producttypeKey}
            </p>
          </div>

          <dl className="grid shrink-0 grid-cols-2 gap-x-6 gap-y-3 pl-6 lg:pl-0">
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400">Freigegebene Themen</dt>
              <dd className="mt-1 font-bold tabular-nums text-slate-900 dark:text-white">
                {formatNumber(funnel.approvedTopicCount)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400">Suchpotenzial</dt>
              <dd className="mt-1 font-bold tabular-nums text-slate-900 dark:text-white">
                {formatNumber(funnel.potentialSum)}
              </dd>
            </div>
          </dl>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-900/40 sm:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Freigegebene Themen
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">Suchpotenzial pro Monat</p>
          </div>

          {isLoading && (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Themen werden geladen…
            </p>
          )}

          {error && (
            <p className="py-6 text-center text-sm text-red-800 dark:text-red-100">
              {error instanceof ApiError || error instanceof ApiContractError
                ? error.message
                : 'Die Themen konnten nicht geladen werden.'}
            </p>
          )}

          {topics && (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
              <table className="w-full min-w-[32rem] text-left text-sm">
                <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-700/60 dark:text-slate-300">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-semibold">
                      Thema
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-semibold">
                      Ranking-Platz
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-semibold">
                      Suchpotenzial
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {topics.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                      >
                        Keine freigegebenen Themen.
                      </td>
                    </tr>
                  ) : (
                    topics.map((topic) => (
                      <tr key={topic.id}>
                        <th
                          scope="row"
                          className="px-4 py-3 font-semibold text-slate-900 dark:text-white"
                        >
                          {topic.topic}
                        </th>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">
                          {topic.rankingPosition !== null
                            ? formatNumber(topic.rankingPosition)
                            : '–'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">
                          {topic.rankingSearchVolume !== null
                            ? formatNumber(topic.rankingSearchVolume)
                            : '–'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </article>
  )
}

export function ApprovedFunnelsPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = positiveInteger(searchParams.get('page'), 1)

  const { data, isLoading, error } = useApprovedFunnelSummaries({ page, pageSize: PAGE_SIZE })

  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE)) : 1
  const { approvedTopicCount, potentialSum } = sumApprovedFunnelRows(data?.items ?? [])

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-sm font-semibold text-sky-700 dark:text-sky-300">
          Freigegebene Potenzial-Trichter
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          Potenziale im Überblick
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Freigegebene Hauptrichter mit ihren freigegebenen Themen. Suchpotenzial ist potenzielle
          monatliche Suchnachfrage, keine Produkt-, Umsatz- oder Conversionzahl: Diese Daten liegen
          in der ausgelieferten Tabelle nicht vor.
        </p>
      </header>

      <FunnelAreaNav />

      <section
        aria-label="Dashboard Kennzahlen"
        className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <dl className="grid grid-cols-3 gap-x-5 sm:gap-x-10">
          {[
            { label: 'Haupttrichter', value: formatNumber(data?.totalCount ?? 0) },
            { label: 'Freigegebene Themen (Seite)', value: formatNumber(approvedTopicCount) },
            { label: 'Suchpotenzial (Seite)', value: formatNumber(potentialSum) },
          ].map((metric) => (
            <div key={metric.label}>
              <dt className="text-[0.68rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {metric.label}
              </dt>
              <dd className="mt-0.5 text-xl font-bold tabular-nums text-slate-900 dark:text-white sm:text-2xl">
                {metric.value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {isLoading && (
        <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
          Freigegebene Trichter werden geladen…
        </p>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            {error instanceof PostgrestError ||
            error instanceof ApiError ||
            error instanceof ApiContractError
              ? error.message
              : 'Die freigegebenen Trichter konnten nicht geladen werden.'}
          </p>
        </div>
      )}

      {data && !error && (
        <section aria-labelledby="approved-funnels-heading">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2
                id="approved-funnels-heading"
                className="text-lg font-bold text-slate-900 dark:text-white"
              >
                Freigegebene Haupttrichter
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Haupttrichter aufklappen, um die freigegebenen Themen zu sehen.
              </p>
            </div>
          </div>

          {data.items.length === 0 ? (
            <p className="rounded-xl border border-slate-200 bg-white py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              Noch keine freigegebenen Hauptrichter.
            </p>
          ) : (
            <div className="space-y-4">
              {data.items.map((funnel) => (
                <FunnelCard key={funnel.producttypeKey} funnel={funnel} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Seite {formatNumber(page)} von {formatNumber(totalPages)}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => {
                    setSearchParams((current) => {
                      const next = new URLSearchParams(current)
                      next.set('page', String(page - 1))
                      return next
                    })
                  }}
                  className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Zurück
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => {
                    setSearchParams((current) => {
                      const next = new URLSearchParams(current)
                      next.set('page', String(page + 1))
                      return next
                    })
                  }}
                  className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
